import * as v from "valibot";

import type { ReferralNotificationTemplate } from "@textclub/common/bridge";

import { instrumentSdkMethod } from "../instrumentation";
import { Record, parseArgs } from "../schema-utils";
import type { ModuleContext } from "./module-context";

const GET_REFERRALS_TIMEOUT = 10 * 1000;

const NonEmptyString = v.pipe(v.string(), v.minLength(1));

const ReferralNotificationVariantSchema = v.object({
  title: v.optional(v.nullable(NonEmptyString)),
  body: NonEmptyString,
  ctaText: NonEmptyString,
  imageReference: v.optional(v.nullable(NonEmptyString)),
});

const ReferralNotificationTemplateSchema = v.object({
  minConversionCount: v.pipe(v.number(), v.integer(), v.minValue(1)),
  variants: v.pipe(v.array(ReferralNotificationVariantSchema), v.minLength(1)),
});

const ShareReferralLinkOpts = v.object({
  reference: v.string(),
  entryPayload: v.optional(Record),
  shareTitle: v.optional(v.string()),
  shareText: v.optional(v.string()),
  onboardingSlug: v.optional(v.string()),
  notificationTemplates: v.optional(
    v.array(ReferralNotificationTemplateSchema),
  ),
  shareImage: v.optional(v.string()),
});

/**
 * The ReferralsModule manages referral operations.
 *
 * Both referral APIs work for registered and guest players.
 */
export class ReferralsModule {
  constructor(private ctx: ModuleContext) {
    this.ctx.registerMessageHandlers({
      ShareReferralLinkResponse: (message) => this.ctx.resolveWaiting(message),
      GetReferralsResponse: (message) => this.ctx.resolveWaiting(message),
    });
  }

  @instrumentSdkMethod({
    name: "referrals.shareReferralLink",
    redact: ["shareImage"],
  })
  async shareReferralLink(options: {
    reference: string;
    entryPayload?: Record<string, unknown>;
    shareTitle?: string;
    shareText?: string;
    onboardingSlug?: string;
    notificationTemplates?: ReferralNotificationTemplate[];
    shareImage?: string;
  }): Promise<{ canceled: boolean }> {
    parseArgs(ShareReferralLinkOpts, options);

    this.ctx.assertInitialized();

    const conversationId = `shareReferralLink_${Date.now()}_${Math.random().toString(36).substring(2)}`;
    this.ctx.sendMessage({
      conversationId,
      type: "ShareReferralLink",
      reference: options.reference,
      entryPayload: options.entryPayload,
      shareTitle: options.shareTitle,
      shareText: options.shareText,
      onboardingSlug: options.onboardingSlug,
      notificationTemplates: options.notificationTemplates,
      shareImage: options.shareImage,
    });

    const response = await this.ctx.waitForNextMessage(
      "ShareReferralLinkResponse",
      conversationId,

      // no timeout, as the user may take an arbitrary amount of time to share
      // or cancel the referral link sharing flow. The UI will ensure that the
      // user can't keep the flow open indefinitely.
      null,
    );

    if (!response.success) {
      throw new Error(response.error);
    }

    return { canceled: response.canceled };
  }

  @instrumentSdkMethod({ name: "referrals.listReferrals" })
  async listReferrals(): Promise<{
    referrals: {
      [reference: string]: { playerId: string; joinedAt: string }[];
    };
    referralsSigned: string;
  }> {
    this.ctx.assertInitialized();

    const conversationId = `getReferrals_${Date.now()}_${Math.random().toString(36).substring(2)}`;
    this.ctx.sendMessage({
      conversationId,
      type: "GetReferrals",
    });

    const response = await this.ctx.waitForNextMessage(
      "GetReferralsResponse",
      conversationId,
      GET_REFERRALS_TIMEOUT,
    );

    if (!response.success) {
      throw new Error(response.error);
    }

    return {
      referrals: response.referrals,
      referralsSigned: response.referralsSigned,
    };
  }

  /** Exposed for decorator use - delegates to context */
  instrumentMethod<T>(opts: { method: string; args: unknown; fn: () => T }): T {
    return this.ctx.instrumentMethod(opts);
  }
}
